/**
 * round11/password-safeguards — the ONE client-side statement of the Cognito
 * password policy, mirroring DockPro-Backend's `AuthStack.ts` `passwordPolicy`
 * (both pools: minLength 8, uppercase, lowercase, digit; symbols NOT
 * required). Cognito remains the authority — these rules exist so a builder
 * sees what is missing BEFORE submitting, and so Cognito's raw
 * "Password did not conform with policy: …" text never reaches the screen.
 *
 * If the pool policy ever changes, change it here in the same PR (the
 * checklist and the on-submit messages both read from PASSWORD_RULES).
 */

export const PASSWORD_MIN_LENGTH = 8;

export type PasswordRuleKey = "length" | "uppercase" | "lowercase" | "number";

export interface PasswordRule {
  key: PasswordRuleKey;
  /** Checklist label, phrased as the thing the password must contain. */
  label: string;
  /** Full-sentence message for the on-submit error when this is the first unmet rule. */
  problem: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    key: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    problem: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
    test: (pw) => pw.length >= PASSWORD_MIN_LENGTH,
  },
  {
    key: "uppercase",
    label: "An uppercase letter (A–Z)",
    problem: "Password needs an uppercase letter.",
    test: (pw) => /[A-Z]/.test(pw),
  },
  {
    key: "lowercase",
    label: "A lowercase letter (a–z)",
    problem: "Password needs a lowercase letter.",
    test: (pw) => /[a-z]/.test(pw),
  },
  {
    key: "number",
    label: "A number (0–9)",
    problem: "Password needs a number.",
    test: (pw) => /[0-9]/.test(pw),
  },
];

export interface PasswordEvaluation {
  rules: Array<{ key: PasswordRuleKey; label: string; ok: boolean }>;
  ok: boolean;
}

export function evaluatePassword(password: string): PasswordEvaluation {
  const rules = PASSWORD_RULES.map((r) => ({ key: r.key, label: r.label, ok: r.test(password) }));
  return { rules, ok: rules.every((r) => r.ok) };
}

/** The message for the first unmet rule, or null when the password passes. */
export function firstPasswordProblem(password: string): string | null {
  const failing = PASSWORD_RULES.find((r) => !r.test(password));
  return failing ? failing.problem : null;
}

/** Leading/trailing whitespace is the classic copy-paste-from-email mistake. */
export function hasEdgeWhitespace(value: string): boolean {
  return value.length > 0 && value !== value.trim();
}

export const GENERIC_PASSWORD_PROBLEM = "Password does not meet requirements.";

/**
 * Turn Cognito's `InvalidPasswordException` text into one of our sentences.
 * Cognito's messages look like "Password did not conform with policy:
 * Password must have uppercase characters" / "… not long enough" /
 * "… must have numeric characters" / "… must have symbol characters".
 */
export function friendlyPasswordPolicyMessage(cognitoMessage?: string | null): string {
  const m = (cognitoMessage ?? "").toLowerCase();
  if (!m) return GENERIC_PASSWORD_PROBLEM;
  if (m.includes("long enough") || m.includes("length")) return PASSWORD_RULES[0].problem;
  if (m.includes("uppercase")) return PASSWORD_RULES[1].problem;
  if (m.includes("lowercase")) return PASSWORD_RULES[2].problem;
  if (m.includes("numeric") || m.includes("number")) return PASSWORD_RULES[3].problem;
  if (m.includes("symbol")) return "Password needs a symbol.";
  if (m.includes("conform") || m.includes("policy")) return GENERIC_PASSWORD_PROBLEM;
  return GENERIC_PASSWORD_PROBLEM;
}
